import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { MESSAGE_TYPES } from '_constants/messages';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import {
  selectErrorByType,
  selectFetchCounterByType,
  selectLoadingByType,
  selectMessagesByType,
} from '_store/messages/selector';
import { getAnnouncements, markMessages, refreshParams } from '_store/messages/slice';
import { selectCustomerId } from '_store/user/selector';

import Paginate from 'components/atomic/Paginate';
import Card from 'components/atomic/layouts/Card';
import Text from 'components/atomic/layouts/Text';

import AnnouncementsList from './AnnouncementsList';

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
`;

const StyledBadge = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  min-width: 28px;

  padding-top: ${theme.spaces.small2X}px;
  padding-bottom: ${theme.spaces.small2X}px;
  padding-left: ${theme.spaces.smallX + 1}px;
  padding-right: ${theme.spaces.smallX + 1}px;

  border: 1px solid ${hexToRGBA(theme.colors.indigo.primary, 0.08)};
  border-radius: 16px;
  background: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};
`;

const StyledLinkContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;

  &:hover {
    div {
      text-decoration: underline;
    }
  }
`;

const StyledFooter = styled.div`
  padding-top: ${theme.spaces.small}px;
`;

const AnnouncementsView = () => {
  const dispatch = useDispatch();
  const customerId = useSelector(selectCustomerId);
  const loading = useSelector(selectLoadingByType(MESSAGE_TYPES.announcement));
  const messages = useSelector(selectMessagesByType(MESSAGE_TYPES.announcement));
  const error = useSelector(selectErrorByType(MESSAGE_TYPES.announcement));
  const fetchCounter = useSelector(selectFetchCounterByType(MESSAGE_TYPES.announcement));

  const handleSelect = (id, wasRead) => {
    if (customerId && id && !wasRead) {
      dispatch(markMessages({ customerId, type: MESSAGE_TYPES.announcement, ids: [id] }));
    }
  };

  const handleAll = () => {
    if (customerId && messages.notReadIds?.length) {
      dispatch(markMessages({ customerId, type: MESSAGE_TYPES.announcement, ids: [...messages.notReadIds] }));
    }
  };

  const handlePageChange = (page) => {
    dispatch(refreshParams({ type: MESSAGE_TYPES.announcement, params: { page } }));
    dispatch(getAnnouncements({ customerId }));
  };

  useEffect(() => {
    if (customerId && !error && !fetchCounter) {
      dispatch(getAnnouncements({ customerId }));
    }
  }, [customerId, error, fetchCounter]);

  return (
    <StyledCard>
      <StyledHeader>
        <StyledTitleContainer>
          <Text inline size={theme.fontSizes.medium} weight={600}>
            Announcements
          </Text>
          <StyledBadge>
            <Text inline size={theme.fontSizes.smallX} weight={600} color={theme.colors.indigo.primary}>
              {messages.notReadIds?.length}
            </Text>
          </StyledBadge>
        </StyledTitleContainer>
        <StyledLinkContainer onClick={handleAll}>
          <Text size={theme.fontSizes.small} weight={600} color={theme.colors.indigo.primary}>
            Read All
          </Text>
        </StyledLinkContainer>
      </StyledHeader>
      <AnnouncementsList
        loading={!messages.elements?.length && loading}
        items={messages.elements}
        onSelect={handleSelect}
      />
      <StyledFooter>
        <Paginate
          pageRangeDisplayed={0}
          pageCount={messages?.totalPages}
          currentPage={messages.page}
          onPageChange={handlePageChange}
        />
      </StyledFooter>
    </StyledCard>
  );
};

export default AnnouncementsView;
